import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, withRouter } from 'react-router-dom';
import { FullScreenSpinner } from '@guryou/html-components';
import { cognitoLogin, cognitoLogout, StateStatus } from '@guryou/redux';
import { authService, checkIfOwner } from '@guryou/utils';
import logo from 'assets/img/logo.png';
import { persistor } from 'store';
import { getManagementLandingPath } from 'utils/landingPage';

const NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED';

const EyeIcon = ({ open }) =>
  open ? (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21"
      />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

const Login = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useSelector(state => state.auth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const el = new URLSearchParams(window.location.search).get('el');
  const pw = new URLSearchParams(window.location.search).get('pw');

  useEffect(() => {
    const init = async () => {
      await dispatch(cognitoLogout());
      if (el && pw) {
        dispatch(cognitoLogin({ email: decodeURIComponent(el), password: decodeURIComponent(pw), persistor }));
      }
    };
    init();
    //eslint-disable-next-line
  }, []);

  if (auth.status === StateStatus.LOADING) {
    return <FullScreenSpinner big />;
  }

  if (auth.status === StateStatus.LOADED && authService.token) {
    if (auth?.data?.completedOnboarding === 'false' && checkIfOwner()) {
      return <Redirect to={{ pathname: '/onboard', state: { from: props.location } }} />;
    }
    return <Redirect to={{ pathname: getManagementLandingPath(), state: { from: props.location } }} />;
  }

  const handleSubmit = async e => {
    e.preventDefault();
    const emailClean = email.replace(/\s/g, '');
    const responseData = await dispatch(cognitoLogin({ email: emailClean, password, persistor }));
    if (responseData === NEW_PASSWORD_REQUIRED) {
      history.push(`/verify/${email}/${password}`);
    }
  };

  const inputClass =
    'w-full px-4 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E6616A]/30 focus:border-[#E6616A] transition-colors duration-150';

  return (
    <div className="min-h-screen relative overflow-hidden bg-brand-bg flex items-center justify-center px-4 py-12">
      {/* Subtle dot-grid texture */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: 'radial-gradient(circle, #25252514 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />

      <div className="relative z-10 w-full max-w-md -translate-y-12">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Guryou" className="h-16 object-contain" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-6 text-center">{t('GENERAL.login')}</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">{t('GENERAL.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="username"
                required
                className={inputClass}
                placeholder={t('GENERAL.email')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">{t('GENERAL.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className={`${inputClass} pr-10`}
                  placeholder={t('GENERAL.password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600 transition-colors duration-150"
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            {auth?.error && (
              <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600">{auth.error.message}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full py-2.5 px-4 bg-[#E6616A] hover:bg-[#d4545d] active:bg-[#c44850] text-white text-sm font-semibold rounded-lg transition-colors duration-150 mt-2"
            >
              {t('GENERAL.login')}
            </button>
          </form>

          <div className="mt-5 flex flex-col items-center gap-2.5">
            <button
              type="button"
              onClick={() => history.push('/forgot-password')}
              className="text-sm font-semibold text-[#E6616A] hover:text-[#d4545d] transition-colors duration-150 bg-transparent border-none cursor-pointer"
            >
              {`${t('GENERAL.passwordForgoten')} ?`}
            </button>
            <button
              type="button"
              onClick={() => history.push('/resend-code')}
              className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors duration-150 bg-transparent border-none cursor-pointer"
            >
              {t('GENERAL.resendActivationLink')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(Login);
